import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cssMap } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { useDebouncedCallback } from 'use-debounce';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidv4 } from 'uuid';

import IconButton from '@atlaskit/button/icon/button';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useDelayedResizeObserver } from '../../hooks/use-delayed-resize-observer';
import { CardVariant } from '../../knowledge-smart-card/knowledge-smart-card';
import { AnimatedContainer } from '../animated/animated-container';
import { useLuminaInteraction } from '../interaction-context';
import { ProjectHighlightCard } from '../sub-agents/components/project-highlights/project-highlight-card';
import { RelatedPersonCard } from '../sub-agents/components/related-people/related-person-card';
import type {
	CarouselElementType,
	ParsedElementType,
	PersonElementType,
	ProjectHighlightElementType,
	SmartLinkElementType,
} from '../types';

import { SmartLinkElement } from './smartlink-element';
import type { ElementPosition } from './types';

const carouselCardWidth = 355; // card width (300px) + gap (space.200 = 16px)
const scrollWidth = (carouselCardWidth + 46) * 2;

const styles = cssMap({
	carouselStyles: {
		width: '100%',
	},
	controlsWithoutTitle: {
		marginTop: token('space.negative.200'),
	},

	scrollContainerStyles: {
		overflowX: 'auto',
		overflowY: 'hidden',
		display: 'block',
		width: '100%',
		scrollbarWidth: 'none',
		scrollBehavior: 'smooth',
	},
	scrollContainerNoScroll: {
		overflowX: 'hidden',
		overflowY: 'hidden',
		display: 'block',
		width: '100%',
	},
	// Single item: full width
	cardContainerOneItem: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		gap: token('space.200'),
	},
	// Two items: equal width columns, no overflow
	cardContainerTwoItems: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: token('space.200'),
	},
	// Default carousel: 3 visible columns, overflow at 4+ items via minWidth constraint
	cardContainerThreePlusItems: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr 1fr',
		gridAutoFlow: 'column',
		gridAutoColumns: '1fr',
		gap: token('space.200'),
	},
	// Two-column carousel: 2 visible columns using flex, overflow at 3+ items
	cardContainerTwoColumn: {
		display: 'flex',
		gap: token('space.200'),
	},
	// Default item sizing for 3-column carousels
	carouselItemStyles: {
		minWidth: '224px',
		maxWidth: '360px',
	},
	// Item sizing for 2-column carousels — fixed width ensures 2 visible with overflow
	carouselItemStylesTwoColumn: {
		flex: '0 0 49%',
	},
	// Item sizing for 3-column carousels with adjustments for foldable container padding
	carouselItemStylesFoldable: {
		minWidth: '216px',
		maxWidth: '252px',
	},
});

export type CarouselElementProps = {
	component: CarouselElementType;
	isFoldable?: boolean;
	isStreaming?: boolean;
	position: ElementPosition;
	sectionKey?: string;
	showArrows?: boolean;
};

type CarouselItemRendererProps = {
	child: ParsedElementType;
	index: number;
	isFoldable: boolean;
	isStreaming?: boolean;
	isTwoColumn?: boolean;
	sectionKey?: string;
	variant?: CardVariant;
};

const CarouselItemRenderer = ({
	child,
	index,
	isFoldable,
	isStreaming,
	isTwoColumn,
	sectionKey,
	variant,
}: CarouselItemRendererProps): React.JSX.Element => {
	const isProjectHighlight = typeof child !== 'string' && child.tag === 'project-highlight-card';
	const isRelatedPeopleCard = typeof child !== 'string' && child.tag === 'people-card';
	const isSmartLink = typeof child !== 'string' && child.tag === 'smart-link';

	if (isProjectHighlight) {
		return (
			<Box
				testId="carousel-item-project-highlight-card"
				xcss={
					isTwoColumn
						? styles.carouselItemStylesTwoColumn
						: isFoldable
							? styles.carouselItemStylesFoldable
							: styles.carouselItemStyles
				}
			>
				<ProjectHighlightCard component={child as ProjectHighlightElementType} />
			</Box>
		);
	}

	if (isRelatedPeopleCard) {
		return (
			<Box
				testId="carousel-item-people-card"
				xcss={
					isTwoColumn
						? styles.carouselItemStylesTwoColumn
						: isFoldable
							? styles.carouselItemStylesFoldable
							: styles.carouselItemStyles
				}
			>
				<RelatedPersonCard component={child as PersonElementType} />
			</Box>
		);
	}

	if (isSmartLink) {
		return (
			<Box
				testId="carousel-item-smart-link"
				xcss={
					isTwoColumn
						? styles.carouselItemStylesTwoColumn
						: isFoldable
							? styles.carouselItemStylesFoldable
							: styles.carouselItemStyles
				}
			>
				<SmartLinkElement
					component={child as SmartLinkElementType}
					variant={variant}
					isStreaming={isStreaming}
					sectionKey={sectionKey}
					containerType="carousel"
					indexInContainer={index}
				/>
			</Box>
		);
	}

	return <Box />;
};

const isScrollAtLeft = (elem: HTMLDivElement | null) => elem?.scrollLeft === 0;
const isScrollAtRight = (elem: HTMLDivElement | null) =>
	elem ? Math.abs(elem?.scrollWidth - elem?.scrollLeft - elem?.clientWidth) <= 1 : false;

export const CarouselElement: React.MemoExoticComponent<
	({
		component,
		position,
		showArrows,
		isFoldable,
		isStreaming,
	}: CarouselElementProps) => React.JSX.Element | null
> = React.memo(
	({
		component,
		position,
		showArrows = true,
		isFoldable = false,
		isStreaming = false,
		sectionKey,
	}: CarouselElementProps): React.JSX.Element | null => {
		const onInteraction = useLuminaInteraction();
		const scrollContainerRef = useRef<HTMLDivElement>(null);
		const [hideLeftScroll, setHideLeftScroll] = useState(true);
		const [hideRightScroll, setHideRightScroll] = useState(false);
		const [isOverflowing, setIsOverflowing] = useState(false);

		// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
		const key_id = useRef(uuidv4());

		const carouselChildren = (component.children || []) as ParsedElementType[];
		const shouldAlignHeader = position === 'single-column';

		// Two-column carousels show 2 wider cards with full-height rendering.
		const isTwoColumn = component.attributes?.display === 'two-column';
		const smartLinkVariant = isTwoColumn ? CardVariant.FULL : CardVariant.COMPACT;

		const updateArrowVisibility = useCallback(() => {
			if (scrollContainerRef.current) {
				setHideLeftScroll(isScrollAtLeft(scrollContainerRef.current));
				setHideRightScroll(isScrollAtRight(scrollContainerRef.current));
			}
		}, []);

		const checkOverflow = useCallback(() => {
			if (scrollContainerRef.current) {
				const { scrollWidth: sw, clientWidth: cw } = scrollContainerRef.current;
				setIsOverflowing(sw > cw);
			}
		}, []);

		// Detect overflow changes when the scroll container is resized
		const handleResize = useCallback<ResizeObserverCallback>(() => {
			checkOverflow();
			updateArrowVisibility();
		}, [checkOverflow, updateArrowVisibility]);

		useDelayedResizeObserver(scrollContainerRef, handleResize, 100, [carouselChildren.length]);

		useEffect(() => {
			const scrollContainer = scrollContainerRef.current;
			if (scrollContainer && carouselChildren.length) {
				checkOverflow();
				updateArrowVisibility();

				const unbind = bind(scrollContainer, {
					type: 'scroll',
					listener: updateArrowVisibility,
				});

				return unbind;
			}
		}, [carouselChildren.length, checkOverflow, updateArrowVisibility]);

		const [onScroll] = useDebouncedCallback(() => {
			updateArrowVisibility();
		}, 30);

		const scrollLeft = () => {
			if (scrollContainerRef.current) {
				scrollContainerRef.current.scrollLeft -= scrollWidth;
				onInteraction?.({ type: 'carouselNavigated', direction: 'previous' });
			}
		};

		const scrollRight = () => {
			if (scrollContainerRef.current) {
				scrollContainerRef.current.scrollLeft += scrollWidth;
				onInteraction?.({ type: 'carouselNavigated', direction: 'next' });
			}
		};

		if (!carouselChildren || carouselChildren.length < 2) {
			return null;
		}

		return (
			<AnimatedContainer isStreaming={isStreaming}>
				<Stack
					xcss={styles.carouselStyles}
					space={shouldAlignHeader ? 'space.0' : 'space.100'}
					testId="lumina-layout-carousel"
				>
					<Inline spread="space-between" grow="fill" alignBlock="center">
						{!shouldAlignHeader && component.attributes?.title && (
							<Text
								weight="semibold"
								size="small"
								color="color.text.accent.gray"
								testId="carousel-title"
							>
								{component.attributes.title}
							</Text>
						)}
						{showArrows && (!hideLeftScroll || !hideRightScroll) && (
							<Inline
								space="space.100"
								grow="fill"
								alignInline="end"
								xcss={shouldAlignHeader && styles.controlsWithoutTitle}
							>
								<IconButton
									icon={ArrowLeftIcon}
									label="scroll left"
									onClick={scrollLeft}
									shape="circle"
									appearance="subtle"
									spacing="compact"
									isDisabled={hideLeftScroll}
								/>
								<IconButton
									icon={ArrowRightIcon}
									label="scroll right"
									onClick={scrollRight}
									shape="circle"
									appearance="subtle"
									spacing="compact"
									isDisabled={hideRightScroll}
								/>
							</Inline>
						)}
					</Inline>
					<Box
						xcss={isOverflowing ? styles.scrollContainerStyles : styles.scrollContainerNoScroll}
						ref={scrollContainerRef}
						onScroll={onScroll}
						tabIndex={isOverflowing ? 0 : undefined}
					>
						<Box
							testId={isTwoColumn ? 'carousel-grid-two-column' : 'carousel-grid-default'}
							xcss={
								isTwoColumn
									? styles.cardContainerTwoColumn
									: carouselChildren.length === 1
										? styles.cardContainerOneItem
										: carouselChildren.length === 2
											? styles.cardContainerTwoItems
											: styles.cardContainerThreePlusItems
							}
						>
							{carouselChildren.map((child, index) => (
								<CarouselItemRenderer
									key={`${key_id.current}-${index}`}
									child={child}
									index={index}
									isFoldable={isFoldable}
									isStreaming={isStreaming}
									isTwoColumn={isTwoColumn}
									sectionKey={sectionKey}
									variant={smartLinkVariant}
								/>
							))}
						</Box>
					</Box>
				</Stack>
			</AnimatedContainer>
		);
	},
);
